/**
 * useCategories Hook
 * React Query hooks for category CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@myvault/shared';
import type {
  Category,
  CategoriesResponse,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  DeleteCategoryRequest,
  ReorderCategoriesRequest,
} from '../types';

// Query keys
const CATEGORIES_QUERY_KEY = ['categories'] as const;

/**
 * Fetch all categories
 */
export function useCategories() {
  return useQuery<CategoriesResponse>({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get<CategoriesResponse>('/categories');
      return response.data;
    },
  });
}

/**
 * Create a new category
 */
export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCategoryRequest) => {
      const response = await apiClient.post<Category>('/categories', data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate categories query to refetch
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
    },
  });
}

/**
 * Update an existing category
 */
export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateCategoryRequest;
    }) => {
      const response = await apiClient.patch<Category>(
        `/categories/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
    },
  });
}

/**
 * Delete a category
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      reassignmentAction,
      targetCategoryId,
    }: {
      id: string;
      reassignmentAction: DeleteCategoryRequest['reassignmentAction'];
      targetCategoryId?: string;
    }) => {
      await apiClient.delete(`/categories/${id}`, {
        data: {
          reassignmentAction,
          targetCategoryId,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
    },
  });
}

/**
 * Reorder categories (for custom categories only)
 */
export function useReorderCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ReorderCategoriesRequest) => {
      await apiClient.post('/categories/reorder', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
    },
  });
}
